import { useState } from "react";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { useDataStore } from "@store/user_org";
import { useAtelierStore } from "@store/atelierStore";
import { atelierApi, type MergeDuplicateChargesItem, type MergeDuplicateChargesResult } from "@services/api/atelier";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { metierLabel } from "../../../types/garage/metier";

const formatHours = (h: number) => `${+(h || 0).toFixed(2)} h`;

function OrdreRow({ item }: { item: MergeDuplicateChargesItem }) {
  const head = [item.ordre_num ? `OR ${item.ordre_num}` : "", item.client_name, item.plate].filter(Boolean).join(" · ");
  const tail = [
    metierLabel(item.specialty),
    `${item.hours_before.map(formatHours).join(" + ")} → ${formatHours(item.hours_after)}`,
    item.basis === "somme" ? "charges additionnées" : "travail de l'ordre moins le déjà posé",
  ].join(" · ");
  return (
    <div className="flex flex-col py-1.5">
      <span className="text-h4 font-medium text-neutral-700">{head || "Sans ordre"}</span>
      <span className="text-h5 text-neutral-400">{tail}</span>
    </div>
  );
}

/** Réparation : la prise de RDV et le devis initial ouvraient chacun une charge « à
 *  répartir » pour le même travail — deux magnets à planifier pour un seul ordre. */
export function MergeDuplicateChargesCard() {
  const userData = useDataStore((s) => s.userData);
  const orgData = useDataStore((s) => s.orgData);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<MergeDuplicateChargesResult | null>(null);
  const [confirming, setConfirming] = useState(false);

  if (!isOwnerOrAdmin(userData)) return null;

  const run = async (dryRun: boolean) => {
    setLoading(true);
    try {
      const result = await atelierApi.mergeDuplicateCharges(dryRun);
      setPreview(result);
      if (!dryRun) {
        showNotification({
          type: "success",
          message: `${result.ordres_count} ordre${result.ordres_count > 1 ? "s" : ""} : charges en double fusionnées`,
        });
        if (orgData?.id) useAtelierStore.getState().fetchData(orgData.id, true);
      }
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Échec de l'opération" });
    } finally {
      setLoading(false);
    }
  };

  const nothing = preview && preview.ordres_count === 0;

  return (
    <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1 max-w-700">
      <div className="flex flex-col">
        <div className="text-h3 font-semibold text-black">
          Planning atelier : fusionner les charges en double d'un même ordre
        </div>
        <div className="text-h4 text-neutral-400">
          Ordres qui ont deux charges «&nbsp;à répartir&nbsp;» du même métier (celle de la
          prise de RDV et celle du devis). Il n'en reste qu'une, dimensionnée sur le
          travail de l'ordre moins ce qui est déjà posé. Les compléments de travaux, les
          lignes MyO et les charges en pause ne sont pas touchés.
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <SecondaryButton variant="small" label={loading ? "En cours..." : "Aperçu"} disabled={loading} onClick={() => run(true)} />
        <Button
          variant="small"
          label="Appliquer"
          disabled={loading || !preview?.dry_run || !!nothing}
          onClick={() => setConfirming(true)}
        />
      </div>
      {preview && (
        <div className="flex flex-col gap-1">
          <div className="text-h4 font-medium text-neutral-600">
            {nothing
              ? "Rien à fusionner."
              : `${preview.dry_run ? "À fusionner" : "Fusionné"} : ${preview.ordres_count} ordre${preview.ordres_count > 1 ? "s" : ""}, ${preview.charges_removed} charge${preview.charges_removed > 1 ? "s" : ""} en trop.`}
          </div>
          {preview.ordres.length > 0 && (
            <div className="flex flex-col divide-y max-h-300 overflow-auto">
              {preview.ordres.map((o) => <OrdreRow key={o.kept_id} item={o} />)}
            </div>
          )}
        </div>
      )}
      {confirming && preview && (
        <ConfirmationModal
          title="Fusionner ces charges ?"
          message={`${preview.charges_removed} charge${preview.charges_removed > 1 ? "s" : ""} en double ser${preview.charges_removed > 1 ? "ont" : "a"} fondue${preview.charges_removed > 1 ? "s" : ""} dans la charge de leur ordre (${preview.ordres_count} ordre${preview.ordres_count > 1 ? "s" : ""}).`}
          confirmLabel="Appliquer"
          onClose={() => setConfirming(false)}
          onConfirm={() => { setConfirming(false); run(false); }}
        />
      )}
    </div>
  );
}
